import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Beaker } from 'lucide-react';
import api from '../services/api';

const DENSITY_CATEGORIES = ['Madeira', 'Metal', 'Vidro', 'Plástico', 'Pedra', 'Têxtil'];

const COLOR_PALETTE = [
  '#080808', '#5a5a5a', '#adadad', '#ffffff',
  '#00144D', '#234C7D', '#4684AC', '#68BDDB',
  '#61C099', '#80D1B7', '#9FE2D5', '#BEF3F2',
  '#183D0B', '#386D1D', '#599D2F', '#79CD40',
  '#cb0200', '#dc2e2d', '#ee5b5a', '#ff8886',
  '#530D54', '#740A5B', '#960661', '#B70268',
  '#F74780', '#FA7BA4', '#FCB1C9', '#FFE5ED',
  '#DD5500', '#E77901', '#F09C02', '#F9C000',
  '#c7b018', '#d7c622', '#e7da2c', '#efe832',
  '#3e1011', '#622e27', '#864c3d', '#aa6a53'
];

const ColorPalette = ({ value, onChange, label = 'Cor' }) => {
  const [showPicker, setShowPicker] = useState(false);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <input
          type="color"
          value={value || '#ffffff'}
          onChange={(e) => onChange(e.target.value)}
          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
        />
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#FFFFFF"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
        />
      </div>

      <button
        type="button"
        onClick={() => setShowPicker(!showPicker)}
        className="text-xs text-blue-600 hover:text-blue-700"
      >
        {showPicker ? 'Ocultar paleta' : 'Mostrar paleta de cores'}
      </button>

      {showPicker && (
        <div className="p-3 border border-gray-200 rounded-lg bg-gray-50">
          <div className="grid grid-cols-10 gap-1">
            {COLOR_PALETTE.map(color => (
              <button
                key={color}
                type="button"
                onClick={() => onChange(color)}
                className={`w-7 h-7 rounded border ${
                  value === color ? 'border-4 border-gray-800' : 'border-gray-300'
                } hover:scale-110 transition-transform`}
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const DensityPicker = ({ value, onChange, densityValue, onDensityValueChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [densities, setDensities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDensity, setSelectedDensity] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadDensities();
    }
  }, [isOpen]);

  useEffect(() => {
    if (value && densities.length > 0) {
      const found = densities.find(d => d.id === value);
      setSelectedDensity(found || null);
    }
  }, [value, densities]);

  const loadDensities = async () => {
    try {
      setLoading(true);
      const response = await api.get('/material-densities');
      if (response.data.success) {
        setDensities(response.data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar densidades:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (density) => {
    onChange(density.id);
    if (onDensityValueChange) {
      onDensityValueChange(density.density);
    }
    setSelectedDensity(density);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = () => {
    onChange(null);
    setSelectedDensity(null);
    if (onDensityValueChange) {
      onDensityValueChange('');
    }
  };

  const filtered = densities.filter(d => {
    if (!search) return true;
    const term = search.toLowerCase();
    return (
      d.name?.toLowerCase().includes(term) ||
      d.category?.toLowerCase().includes(term)
    );
  });

  const grouped = DENSITY_CATEGORIES.reduce((acc, cat) => {
    acc[cat] = filtered.filter(d => d.category === cat);
    return acc;
  }, {});

  return (
    <div className="relative" ref={containerRef}>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`
            flex-1 flex items-center justify-between px-3 py-2 border rounded-lg text-sm transition-colors
            ${selectedDensity
              ? 'bg-blue-50 border-blue-200 text-blue-800'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }
          `}
        >
          <span className="truncate flex items-center gap-2">
            <Beaker className="w-4 h-4 flex-shrink-0" />
            {selectedDensity ? (
              <>
                <span className="font-medium">{selectedDensity.name}</span>
                <span className="text-xs text-gray-500">{selectedDensity.density} kg/m³</span>
              </>
            ) : (
              <span className="text-gray-400">Selecionar material...</span>
            )}
          </span>
          {selectedDensity && (
            <X
              className="w-4 h-4 text-blue-600 cursor-pointer"
              onClick={(e) => { e.stopPropagation(); handleClear(); }}
            />
          )}
        </button>
      </div>

      {isOpen && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
          <div className="p-2 border-b border-gray-100 bg-gray-50">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar material..."
                className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm"
                autoFocus
              />
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="px-4 py-6 text-center text-sm text-gray-500">Carregando...</div>
            ) : filtered.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-500">Nenhum material encontrado</div>
            ) : (
              Object.entries(grouped).map(([category, items]) => (
                items.length > 0 && (
                  <div key={category}>
                    <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50 border-b border-gray-100">
                      {category}
                    </div>
                    {items.map(d => (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => handleSelect(d)}
                        className={`w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between ${
                          value === d.id ? 'bg-blue-50' : ''
                        }`}
                      >
                        <span className="text-sm text-gray-900">{d.name}</span>
                        <span className="text-xs text-gray-500">{d.density} kg/m³</span>
                      </button>
                    ))}
                  </div>
                )
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export { ColorPalette, DensityPicker };
export default DensityPicker;